import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Target, Crosshair, XCircle } from "lucide-react";

const statusConfig = {
  used: { icon: Target, bg: "bg-green-50 border-green-200", text: "text-green-700", label: "Used", barColor: "bg-green-500" },
  partially_used: { icon: Crosshair, bg: "bg-yellow-50 border-yellow-200", text: "text-yellow-700", label: "Partial", barColor: "bg-yellow-500" },
  noise: { icon: XCircle, bg: "bg-red-50 border-red-200", text: "text-red-700", label: "Noise", barColor: "bg-red-400" },
};

export default function ChunkAttribution({ attributions, chunks }) {
  if (!attributions || attributions.length === 0 || !chunks) return null;

  const usedCount = attributions.filter(a => a.status === "used").length;
  const noiseCount = attributions.filter(a => a.status === "noise").length;

  return (
    <Card className="border border-border shadow-none" data-testid="chunk-attribution-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Target className="w-5 h-5 text-muted-foreground" />
            Chunk Attribution
          </CardTitle>
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="font-mono bg-green-50 text-green-700 border-green-200">
              {usedCount} used
            </Badge>
            <Badge variant="outline" className="font-mono bg-red-50 text-red-700 border-red-200">
              {noiseCount} noise
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-[300px] pr-2">
          <div className="space-y-2">
            {attributions.map((attr, idx) => {
              const cfg = statusConfig[attr.status] || statusConfig.noise;
              const Icon = cfg.icon;
              const chunk = chunks[attr.chunk_index];
              const influencePercent = Math.round((attr.influence_score || 0) * 100);

              return (
                <div
                  key={idx}
                  className={`border rounded-sm p-3 ${cfg.bg}`}
                  data-testid={`attribution-${attr.chunk_index}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${cfg.text}`} />
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Chunk {attr.chunk_index}
                      </span>
                      <Badge variant="outline" className={`text-[10px] font-mono ${cfg.text}`}>
                        {cfg.label}
                      </Badge>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">
                      {influencePercent}% influence
                    </span>
                  </div>

                  {/* Influence bar */}
                  <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all ${cfg.barColor}`}
                      style={{ width: `${influencePercent}%` }}
                    />
                  </div>

                  {chunk && (
                    <p className="text-xs leading-relaxed line-clamp-2 opacity-75">
                      {chunk.content}
                    </p>
                  )}

                  {attr.attributed_claims?.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      <span className="text-[10px] text-muted-foreground">Supports:</span>
                      {attr.attributed_claims.map(ci => (
                        <Badge key={ci} variant="outline" className="text-[10px] px-1 py-0 h-4 font-mono">
                          Claim {ci}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
